import { useMemo, useState } from 'react';
import { Trophy } from 'lucide-react';
import { useHistoricalPerformance, HistoryRow } from '../../../queries/meetingAnalysis';
import { formatRoi, winPercentage, winPercentageClass } from '../../../lib/history-performance';
import { formatHistoryDate, formatHistoryWeekday, groupHistoryByDay } from './race-drilldown-helpers';
import { getConditionStyle } from '../meetings-helpers';
import { RaceMatrixView } from './RaceMatrixView';

interface HistoricalPerformanceViewProps {
  meetingId: string;
  currentMeetingDate?: string;
}

interface SelectableMeeting {
  id: string;
  date: string | undefined;
  isCurrent: boolean;
}

const modelLabel = (row: HistoryRow) => [row.ai_model, row.ai_version].filter(Boolean).join(' ') || 'Unknown model';

/** "3/8 (38%) +12.5%" — winners over races, strike %, and ROI, colour-coded. */
function StrikeStat({ label, winners, races, roi }: { label: string; winners: number | string | null; races: number | string; roi: number | string | null }) {
  const pct = winPercentage(winners ?? 0, races);
  const r = formatRoi(roi);
  return (
    <span className="whitespace-nowrap">
      <span className="text-muted-foreground">{label}</span> <span className="font-semibold text-foreground">{winners ?? 0}/{races}</span>{' '}
      <span className={winPercentageClass(pct)}>({pct}%)</span> <span className={r.className}>{r.text}</span>
    </span>
  );
}

function DayCard({ group }: { group: ReturnType<typeof groupHistoryByDay>[number] }) {
  const [expanded, setExpanded] = useState(false);
  const { winner, rows, contested } = group;
  const condition = [winner.track_condition, winner.track_condition_number].filter(Boolean).join(' ');

  return (
    <div className="rounded-lg border border-border bg-card p-3 shadow-sm">
      {/* Line 1: date · races · winning model (+ condition) */}
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm">
          {contested && <Trophy className="h-3.5 w-3.5 shrink-0 text-emerald-600" />}
          {formatHistoryWeekday(group.date) && <span className="text-muted-foreground">{formatHistoryWeekday(group.date)}</span>}
          <span className="font-semibold">{formatHistoryDate(group.date)}</span>
          <span className="text-muted-foreground">· {winner.total_races} races ·</span>
          <span className="rounded bg-emerald-100 px-1.5 py-0.5 font-mono text-xs font-medium text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400">
            {modelLabel(winner)}
          </span>
        </div>
        {condition && (
          <span className={`inline-flex items-center rounded-lg border px-2.5 py-1 text-xs font-medium ${getConditionStyle(winner.track_condition)}`}>
            {condition}
          </span>
        )}
      </div>

      {/* Line 2: top / total / value metrics */}
      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
        <StrikeStat label="Top" winners={winner.top_1_winners} races={winner.total_races} roi={winner.top_1_roi} />
        <span className="whitespace-nowrap">
          <span className="text-muted-foreground">Total</span> <span className="font-semibold text-foreground">{winner.all_picks_winners ?? '—'}</span>
        </span>
        <StrikeStat label="Value" winners={winner.value_winners} races={winner.total_races} roi={winner.value_roi} />
      </div>

      {contested && (
        <>
          <button type="button" onClick={() => setExpanded((v) => !v)} className="mt-1.5 text-xs font-medium text-primary hover:underline">
            {expanded ? 'Hide other models' : `Compare ${rows.length} models`}
          </button>
          {expanded && (
            <div className="mt-2 space-y-1.5 border-t border-border pt-2">
              {rows.slice(1).map((row, i) => (
                <div key={`${row.ai_model}-${row.ai_version}-${i}`} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
                  <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-muted-foreground">{modelLabel(row)}</span>
                  <StrikeStat label="Top" winners={row.top_1_winners} races={row.total_races} roi={row.top_1_roi} />
                  <span className="whitespace-nowrap">
                    <span className="text-muted-foreground">Total</span> <span className="font-semibold text-foreground">{row.all_picks_winners ?? '—'}</span>
                  </span>
                  <StrikeStat label="Value" winners={row.value_winners} races={row.total_races} roi={row.value_roi} />
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function HistoricalPerformanceView({ meetingId, currentMeetingDate }: HistoricalPerformanceViewProps) {
  const { data: rows = [], isLoading } = useHistoricalPerformance(meetingId);
  const days = useMemo(() => groupHistoryByDay(rows), [rows]);
  const [view, setView] = useState<'results' | 'matrix'>('results');
  const [selectedMatrixMeetingId, setSelectedMatrixMeetingId] = useState<string | null>(null);

  // Current meeting first, then every other meeting that appears in the
  // history rows - so the matrix can be viewed for any past date on this track
  // (the old admin's History-popup matrix selector).
  const uniqueMeetings = useMemo<SelectableMeeting[]>(() => {
    const others = Array.from(
      new Map(
        rows
          .filter((r) => r.meeting_id !== meetingId)
          .map((r) => [r.meeting_id, { id: r.meeting_id, date: r.meeting_date, isCurrent: false }]),
      ).values(),
    );
    return [{ id: meetingId, date: currentMeetingDate, isCurrent: true }, ...others];
  }, [rows, meetingId, currentMeetingDate]);

  const activeMatrixMeetingId = selectedMatrixMeetingId ?? meetingId;

  const viewToggle = (
    <div className="mb-3 flex gap-1">
      {(['results', 'matrix'] as const).map((v) => (
        <button
          key={v}
          type="button"
          onClick={() => setView(v)}
          className={`rounded-md px-3 py-1.5 text-sm font-medium ${view === v ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-accent'}`}
        >
          {v === 'results' ? 'Results' : 'Race matrix'}
        </button>
      ))}
    </div>
  );

  if (isLoading) {
    return <div className="py-8 text-center text-muted-foreground">Loading history…</div>;
  }

  if (view === 'matrix') {
    return (
      <div>
        {viewToggle}
        <div className="mb-4 flex flex-wrap gap-2">
          {uniqueMeetings.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => setSelectedMatrixMeetingId(m.id)}
              className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium ${
                activeMatrixMeetingId === m.id ? 'border-primary bg-primary text-primary-foreground' : 'border-border text-muted-foreground hover:bg-accent'
              }`}
            >
              {m.date ? formatHistoryDate(m.date) : m.id}
              {m.isCurrent && (
                <span className={`rounded px-1 text-[10px] font-semibold ${activeMatrixMeetingId === m.id ? 'bg-white/25 text-white' : 'bg-primary/10 text-primary'}`}>
                  CURRENT
                </span>
              )}
            </button>
          ))}
        </div>
        <RaceMatrixView meetingId={activeMatrixMeetingId} />
      </div>
    );
  }

  if (days.length === 0) {
    return (
      <div>
        {viewToggle}
        <div className="py-8 text-center text-muted-foreground">
          No settled results yet for this track. Switch to <span className="font-medium">Race matrix</span> to see which
          model is in each slot.
        </div>
      </div>
    );
  }

  return (
    <div>
      {viewToggle}
      <div className="space-y-2">
        {days.map((group) => (
          <DayCard key={group.meetingId} group={group} />
        ))}
      </div>
    </div>
  );
}
